import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import date from "date-and-time";
import { sendPasswordResetEmail, signOut, updateProfile } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";

import Card from "../../components/shared/Card/Card";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useAuth from "../../hooks/useAuth";

export default function ProfilePage() {
  const { auth, user, loading } = useAuth();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [displayName, setDisplayName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  useEffect(() => {
    setInfo({
      name: "My Profile",
      desc: "Your account details and sign in settings.",
      pageTitle: "My Profile",
    });
  }, [setInfo]);

  useEffect(() => {
    setDisplayName(user?.displayName ?? "");
  }, [user?.displayName]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  const handleSaveDisplayName = async () => {
    if (!auth.currentUser) return;

    try {
      setIsSavingName(true);
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      addNotification({ variant: "success", primaryText: "Profile updated" });
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to update profile",
      });
    } finally {
      setIsSavingName(false);
    }
  };

  // A reset email avoids the recent-login requirement that in-app password
  // changes carry.
  const handlePasswordReset = async () => {
    if (!user?.email) return;

    try {
      setIsSendingReset(true);
      await sendPasswordResetEmail(auth, user.email);
      addNotification({
        variant: "success",
        primaryText: `Password reset email sent to ${user.email}`,
      });
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to send password reset email",
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      queryClient.clear();
      navigate("/login");
    } catch {
      addNotification({ variant: "error", primaryText: "Failed to sign out" });
    }
  };

  const formatDate = (value?: string) =>
    value ? date.format(new Date(value), "DD/MM/YYYY HH:mm") : "Unknown";

  return (
    <div className="space-y-4">
      <Card title="Account" bodyClassName="p-4 space-y-4">
        <div className="flex items-center gap-4">
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName ?? "Profile"}
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-600 text-lg font-semibold text-white">
              {(user?.displayName || user?.email || "?")
                .split(/[\s@.]+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0]?.toUpperCase())
                .join("")}
            </div>
          )}
          <div>
            <p className="font-medium text-slate-800">
              {user?.displayName || "No display name set"}
            </p>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div>
            <p className="text-gray-500">User ID</p>
            <p className="break-all font-medium">{user?.uid}</p>
          </div>
          <div>
            <p className="text-gray-500">Sign in provider</p>
            <p className="font-medium">
              {user?.providerData?.[0]?.providerId ?? "Unknown"}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Account created</p>
            <p className="font-medium">
              {formatDate(user?.metadata?.creationTime)}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Last sign in</p>
            <p className="font-medium">
              {formatDate(user?.metadata?.lastSignInTime)}
            </p>
          </div>
        </div>
      </Card>

      <Card title="Display Name" bodyClassName="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Display name
            </label>
            <input
              type="text"
              className="w-72 rounded border border-gray-300 px-3 py-2 text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How your name appears on tickets"
            />
          </div>
          <Button
            onClick={handleSaveDisplayName}
            disabled={
              isSavingName ||
              !displayName.trim() ||
              displayName.trim() === (user?.displayName ?? "")
            }
          >
            {isSavingName ? "Saving..." : "Save"}
          </Button>
        </div>
      </Card>

      <Card title="Security" bodyClassName="p-4 space-y-3">
        <p className="text-sm text-gray-600">
          We'll email you a link to set a new password.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outlined"
            onClick={handlePasswordReset}
            disabled={isSendingReset || !user?.email}
          >
            {isSendingReset ? "Sending..." : "Send password reset email"}
          </Button>
          <Button variant="outlined" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
